import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ShieldCheck,
  Calendar,
  Users,
  Building2,
  KeyRound,
  Sparkles,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Download,
  RefreshCw,
  CreditCard,
  Headphones,
  Mail,
  Phone,
  MessageCircle,
  Globe,
  Wallet,
  Receipt,
  ExternalLink,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";

// Halaman placeholder Manajemen Aktivasi.
// Tidak terhubung ke API/backend (belum tersedia) — semua data adalah dummy.
// Pertahankan struktur ringan agar mudah di-wire ke API saat tersedia.
const LICENSE_INFO = {
  status: "Aktif",
  plan: "Business Pro",
  licenseKey: "ASA-LINK-2026-XXXX-PRO-9F3D-77B2",
  activatedAt: "10 April 2026",
  expiredAt: "9 April 2027",
  daysRemaining: 345,
  maxStores: 5,
  usedStores: 2,
  maxUsers: 50,
  usedUsers: 12,
};

const HISTORY = [
  {
    id: 1,
    date: "10 April 2026",
    type: "Aktivasi",
    plan: "Business Pro",
    by: "Admin Pusat",
    note: "Lisensi tahunan diperpanjang",
  },
  {
    id: 2,
    date: "11 April 2025",
    type: "Perpanjangan",
    plan: "Business Pro",
    by: "Admin Pusat",
    note: "Auto-renew sukses",
  },
  {
    id: 3,
    date: "10 April 2024",
    type: "Upgrade",
    plan: "Starter \u2192 Business Pro",
    by: "Owner",
    note: "Upgrade plan",
  },
  {
    id: 4,
    date: "10 April 2023",
    type: "Aktivasi",
    plan: "Starter",
    by: "Owner",
    note: "Aktivasi pertama kali",
  },
];

const BILLING = {
  nextBillingDate: "9 April 2027",
  amount: "Rp 4.800.000",
  cycle: "Tahunan",
  method: "Transfer Bank \u2014 BCA \u2022\u2022\u2022\u2022 7821",
  autoRenew: true,
  invoiceCount: 4,
};

const SUPPORT_CONTACTS = [
  {
    icon: "mail",
    label: "Email Support",
    value: "support@asalink.id",
    href: "mailto:support@asalink.id",
  },
  {
    icon: "whatsapp",
    label: "WhatsApp",
    value: "+62 812-3456-7890",
    href: "https://wa.me/6281234567890",
  },
  {
    icon: "phone",
    label: "Call Center",
    value: "(021) 5000-1234",
    href: "tel:+622150001234",
  },
  {
    icon: "web",
    label: "Pusat Bantuan",
    value: "help.asalink.id",
    href: "https://help.asalink.id",
  },
];

export default function ActivationManagement() {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const sidebarToggle = () =>
    setIsSidebarCollapsed((prev) => {
      const newState = !prev;
      localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
      return newState;
    });

  const usagePercent = (used, max) =>
    max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={sidebarToggle}
        activePage="konfigurasi"
      />

      <div
        className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header />

        <div className="max-w-full p-6 lg:p-8">
          {/* === Hero Header === */}
          <div className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-2xl p-4 lg:p-5 mb-5 overflow-hidden shadow-lg shadow-teal-900/10">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-10 w-48 h-48 bg-emerald-300/10 rounded-full blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  type="button"
                  onClick={() => navigate("/konfigurasi")}
                  aria-label="Kembali"
                  title="Kembali ke Konfigurasi"
                  className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 ring-1 ring-white/20 flex items-center justify-center text-white transition-colors flex-shrink-0">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="min-w-0">
                  <h1 className="text-lg lg:text-xl font-bold text-white tracking-tight leading-tight break-words">
                    Manajemen Aktivasi
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                    <span className="inline-flex items-center gap-1 text-[10px] text-teal-50 bg-white/10 px-2 py-0.5 rounded-full ring-1 ring-white/20">
                      <KeyRound className="w-2.5 h-2.5" /> Paket{" "}
                      {LICENSE_INFO.plan}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] text-teal-50 bg-white/10 px-2 py-0.5 rounded-full ring-1 ring-white/20">
                      <Calendar className="w-2.5 h-2.5" /> Aktif sampai{" "}
                      {LICENSE_INFO.expiredAt} ({LICENSE_INFO.daysRemaining}{" "}
                      hari lagi)
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ring-1 bg-emerald-400/20 text-emerald-50 ring-emerald-300/40">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                  {LICENSE_INFO.status}
                </span>
                <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-teal-700 transition-colors bg-white shadow-md rounded-lg hover:bg-teal-50 shadow-teal-900/20">
                  <RefreshCw className="w-4 h-4" />
                  Perpanjang Lisensi
                </button>
              </div>
            </div>
          </div>

          {/* Stat Cards */}
          <div className="grid grid-cols-1 gap-4 mb-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={<Sparkles className="w-5 h-5" />}
              iconBg="bg-teal-50"
              iconColor="text-teal-600"
              label="Paket Aktif"
              value={LICENSE_INFO.plan}
              hint="Diaktifkan 10 Apr 2026"
              accent
            />
            <StatCard
              icon={<Clock className="w-5 h-5" />}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              label="Sisa Masa Aktif"
              value={`${LICENSE_INFO.daysRemaining} hari`}
              hint={`Berakhir ${LICENSE_INFO.expiredAt}`}
            />
            <StatCard
              icon={<Building2 className="w-5 h-5" />}
              iconBg="bg-violet-50"
              iconColor="text-violet-600"
              label="Penggunaan Toko"
              value={`${LICENSE_INFO.usedStores} / ${LICENSE_INFO.maxStores}`}
              hint={
                <ProgressBar
                  percent={usagePercent(
                    LICENSE_INFO.usedStores,
                    LICENSE_INFO.maxStores,
                  )}
                  color="violet"
                />
              }
            />
            <StatCard
              icon={<Users className="w-5 h-5" />}
              iconBg="bg-sky-50"
              iconColor="text-sky-600"
              label="Penggunaan User"
              value={`${LICENSE_INFO.usedUsers} / ${LICENSE_INFO.maxUsers}`}
              hint={
                <ProgressBar
                  percent={usagePercent(
                    LICENSE_INFO.usedUsers,
                    LICENSE_INFO.maxUsers,
                  )}
                  color="sky"
                />
              }
            />
          </div>

          {/* License Info + Features */}
          <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-3">
            {/* License key card */}
            <div className="p-5 bg-white shadow-sm lg:col-span-2 rounded-2xl ring-1 ring-slate-200/70">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 text-teal-600 rounded-xl bg-teal-50">
                  <KeyRound className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900">
                    Informasi Lisensi
                  </h2>
                  <p className="text-xs text-slate-500">
                    Detail aktivasi yang sedang aktif untuk akun Anda.
                  </p>
                </div>
              </div>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <InfoRow
                  label="Status"
                  value={
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-xs font-semibold">
                      <CheckCircle2 className="w-3 h-3" /> {LICENSE_INFO.status}
                    </span>
                  }
                />
                <InfoRow label="Paket Berlangganan" value={LICENSE_INFO.plan} />
                <InfoRow
                  label="Tanggal Aktivasi"
                  value={LICENSE_INFO.activatedAt}
                />
                <InfoRow
                  label="Tanggal Berakhir"
                  value={LICENSE_INFO.expiredAt}
                />
                <div className="sm:col-span-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Lisensi Key
                  </p>
                  <div className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-slate-50 ring-1 ring-slate-200">
                    <code className="font-mono text-sm truncate text-slate-700">
                      {LICENSE_INFO.licenseKey}
                    </code>
                    <button className="text-xs px-2.5 py-1 rounded-lg bg-white ring-1 ring-slate-200 hover:bg-slate-100 text-slate-600 font-medium">
                      Salin
                    </button>
                  </div>
                </div>
              </dl>

              <div className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-50 ring-1 ring-amber-200/70 text-amber-800 text-xs">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <p>
                  Halaman ini bersifat <strong>placeholder</strong>. Endpoint
                  lisensi belum tersedia di backend. Data ditampilkan dari data
                  contoh.
                </p>
              </div>
            </div>

            {/* Tagihan & Dukungan */}
            <div className="p-5 bg-white shadow-sm rounded-2xl ring-1 ring-slate-200/70">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-violet-50 text-violet-600">
                  <Wallet className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900">
                    Tagihan Berikutnya
                  </h2>
                  <p className="text-xs text-slate-500">
                    Ringkasan pembayaran & dukungan.
                  </p>
                </div>
              </div>

              {/* Next billing summary */}
              <div className="p-3.5 rounded-xl bg-gradient-to-br from-violet-50 to-violet-100/50 ring-1 ring-violet-200/60 mb-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-700/70">
                      Jatuh Tempo
                    </p>
                    <p className="text-sm font-semibold text-slate-900 mt-0.5">
                      {BILLING.nextBillingDate}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${
                      BILLING.autoRenew
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : "bg-slate-100 text-slate-600 ring-slate-200"
                    }`}>
                    <RefreshCw className="w-2.5 h-2.5" />
                    {BILLING.autoRenew ? "Auto-renew" : "Manual"}
                  </span>
                </div>
                <div className="pt-3 mt-3 border-t border-violet-200/60">
                  <p className="text-2xl font-bold leading-none text-violet-700">
                    {BILLING.amount}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Siklus penagihan {BILLING.cycle.toLowerCase()}
                  </p>
                </div>
              </div>

              {/* Payment method + invoices */}
              <div className="grid grid-cols-1 gap-2 mb-4">
                <div className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-slate-50 ring-1 ring-slate-200/70">
                  <div className="flex items-center min-w-0 gap-2">
                    <CreditCard className="flex-shrink-0 w-4 h-4 text-slate-500" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        Metode Pembayaran
                      </p>
                      <p className="text-xs font-medium truncate text-slate-700">
                        {BILLING.method}
                      </p>
                    </div>
                  </div>
                </div>
                <button className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 ring-1 ring-slate-200/70 transition-colors text-left">
                  <div className="flex items-center gap-2">
                    <Receipt className="w-4 h-4 text-slate-500" />
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                        Faktur
                      </p>
                      <p className="text-xs font-medium text-slate-700">
                        {BILLING.invoiceCount} faktur tersedia
                      </p>
                    </div>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                </button>
              </div>

              {/* Support */}
              <div className="pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2 mb-2.5">
                  <Headphones className="w-4 h-4 text-teal-600" />
                  <p className="text-xs font-semibold text-slate-700">
                    Butuh bantuan?
                  </p>
                </div>
                <ul className="space-y-1.5">
                  {SUPPORT_CONTACTS.map((c) => (
                    <li key={c.label}>
                      <a
                        href={c.href}
                        target={
                          c.href.startsWith("http") ? "_blank" : undefined
                        }
                        rel="noreferrer"
                        className="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg hover:bg-teal-50/60 transition-colors group">
                        <span className="flex items-center min-w-0 gap-2">
                          <span className="flex items-center justify-center flex-shrink-0 transition-colors rounded-lg w-7 h-7 bg-slate-100 group-hover:bg-teal-100 text-slate-500 group-hover:text-teal-600">
                            {c.icon === "mail" && (
                              <Mail className="w-3.5 h-3.5" />
                            )}
                            {c.icon === "whatsapp" && (
                              <MessageCircle className="w-3.5 h-3.5" />
                            )}
                            {c.icon === "phone" && (
                              <Phone className="w-3.5 h-3.5" />
                            )}
                            {c.icon === "web" && (
                              <Globe className="w-3.5 h-3.5" />
                            )}
                          </span>
                          <span className="min-w-0">
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                              {c.label}
                            </p>
                            <p className="text-xs font-medium truncate text-slate-700">
                              {c.value}
                            </p>
                          </span>
                        </span>
                        <ExternalLink className="w-3 h-3 transition-colors text-slate-300 group-hover:text-teal-500" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* History */}
          <div className="overflow-hidden bg-white shadow-sm rounded-2xl ring-1 ring-slate-200/70">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-sky-50 text-sky-600">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-slate-900">
                    Riwayat Aktivasi
                  </h2>
                  <p className="text-xs text-slate-500">
                    {HISTORY.length} catatan aktivasi & perpanjangan
                  </p>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-slate-50/80 border-slate-200">
                  <tr>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Tanggal
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Tipe
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Paket
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Oleh
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Keterangan
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {HISTORY.map((h) => (
                    <tr
                      key={h.id}
                      className="transition-colors hover:bg-slate-50/70">
                      <td className="px-5 py-3 text-slate-700">{h.date}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-teal-50 text-teal-700 ring-1 ring-teal-200 text-xs font-semibold">
                          {h.type}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-700">{h.plan}</td>
                      <td className="px-5 py-3 text-slate-700">{h.by}</td>
                      <td className="px-5 py-3 text-slate-500">{h.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </p>
      <div className="text-sm font-medium text-slate-800">{value}</div>
    </div>
  );
}

function ProgressBar({ percent, color = "teal" }) {
  const map = {
    teal: "bg-teal-500",
    violet: "bg-violet-500",
    sky: "bg-sky-500",
    amber: "bg-amber-500",
  };
  return (
    <div className="mt-1.5">
      <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${map[color] || map.teal}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="text-[10px] text-slate-400 mt-1">{percent}% terpakai</p>
    </div>
  );
}

function StatCard({
  icon,
  iconBg,
  iconColor,
  label,
  value,
  hint,
  accent = false,
}) {
  return (
    <div
      className={`relative bg-white p-5 rounded-2xl ring-1 ring-slate-200/70 shadow-sm hover:shadow-md hover:ring-slate-300/60 transition-all overflow-hidden ${
        accent
          ? "ring-teal-200/70 bg-gradient-to-br from-white to-teal-50/40"
          : ""
      }`}>
      <div className="flex items-start justify-between mb-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg} ${iconColor}`}>
          {icon}
        </div>
      </div>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </p>
      <div className="text-xl font-bold leading-tight truncate text-slate-900">
        {value}
      </div>
      {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}
